<script setup lang="ts">
import { computed, ref } from "vue";
import { useExplorerStore } from "@/modules/explorer/store";
import { toastApiResult } from "@/toaster";
import { useApiClient } from "@/api/client";
import { promptInvalidPermissions } from "@/api/helpers/loadingWrapper";
import { useDashboardRedirect } from "@/modules/explorer/composables/useDashboardRedirect";

const { redirectBackToDashboard } = useDashboardRedirect();

const emit = defineEmits<{
	(e: "saveAsNew"): void;
	(e: "overwrite"): void;
	(e: "close"): void;
}>();

defineProps<{
	message?: string;
}>();

const isDialogVisible = defineModel<boolean>();

const saveAsNew = ref(false);

const originalSavedDocument = computed(() => {
	return useExplorerStore().explorerState.lastOpenedSavedDocument;
});

const updateStoredDocumentDocument = async () => {
	if (originalSavedDocument.value) {
		// useExplorerStore().explorerState.lastOpenedSavedDocumentDocumentUuid =
		// 	useExplorerStore().document.uuid;

		const response = await toastApiResult(
			promptInvalidPermissions(
				useApiClient("documents").updateStoredDocumentDocument(
					originalSavedDocument.value,
					useExplorerStore().document.uuid
				)
			)
		);

		if (response.isOk()) {
			useExplorerStore().document.uuid = response.value.active_document_uuid;
			useExplorerStore().explorerState.lastOpenedSavedDocumentDocumentUuid =
				response.value.active_document_uuid;
		}

		if (response.isErr()) {
			useExplorerStore().explorerState.lastOpenedSavedDocumentDocumentUuid =
				null;
		}
	}
	isDialogVisible.value = false;
};

const isPeriodDynamic = computed(() => {
	return (
		useExplorerStore().document.period_selection.from.includes("now") ||
		useExplorerStore().document.period_selection.to.includes("now")
	);
});

const saveConfiguration = () => {
	if (saveAsNew.value) {
		emit("saveAsNew");
		isDialogVisible.value = false;
	} else {
		updateStoredDocumentDocument();
		emit("overwrite");
		redirectBackToDashboard();
	}
	emit("close");
};
</script>

<template>
	<v-dialog v-model="isDialogVisible" width="600">
		<v-card
			class="help"
			prepend-icon="mdi-floppy"
			title="Save Configuration"
			:subtitle="message"
		>
			<v-divider />
			<div class="pa-5 pb-0">
				<div class="pb-2">
					<label>Replace or save as new?</label>
				</div>
				<div>
					<v-radio-group v-model="saveAsNew">
						<v-radio :value="false">
							<template v-slot:label>
								<div>
									Replace original configuration: "<strong>{{
										originalSavedDocument?.title
									}}</strong
									>"
								</div>
							</template>
						</v-radio>
						<v-radio
							:value="true"
							:label="`Save as new configuration`"
						></v-radio>
					</v-radio-group>
				</div>
			</div>
			<div>
				<v-alert
					v-if="!isPeriodDynamic"
					color="warning"
					border="start"
					variant="tonal"
					class="ma-5 mt-0"
					icon="fa:fa-thin fa-file-exclamation"
					prominent
				>
					Your configuration does not include any dynamic dates. While this
					isn't required, incorporating dynamic dates is a common practice for
					managing configurations effectively.
				</v-alert>
			</div>
			<v-divider />
			<v-card-actions>
				<v-spacer />
				<v-btn color="error" variant="text" @click="isDialogVisible = false"
					>Cancel
				</v-btn>
				<v-btn color="secondary" variant="flat" @click="saveConfiguration">
					{{ !saveAsNew ? "Save" : "Next" }}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<style scoped lang="scss"></style>
